import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getEducatorCourses, deleteCourse, toggleCoursePublish } from '../../api/educentialApi';
import './CourseList.css';

function CourseList() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    category: '',
    level: '',
    published: '',
    search: '',
  });
  const [viewMode, setViewMode] = useState('grid'); // grid or list

  useEffect(() => {
    loadCourses();
  }, [filters]);

  const loadCourses = async () => {
    try {
      setLoading(true);
      const response = await getEducatorCourses(filters);
      if (response.success) {
        setCourses(response.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (courseId, courseTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${courseTitle}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteCourse(courseId);
      setCourses(courses.filter((c) => c.id !== courseId));
    } catch (err) {
      alert('Failed to delete course: ' + err.message);
    }
  };

  const handleTogglePublish = async (courseId) => {
    try {
      const response = await toggleCoursePublish(courseId);
      if (response.success) {
        setCourses(
          courses.map((c) =>
            c.id === courseId ? { ...c, published: response.data.published } : c
          )
        );
      }
    } catch (err) {
      alert('Failed to update publish status: ' + err.message);
    }
  };

  const calculateStats = () => {
    const total = courses.length;
    const published = courses.filter((c) => c.published).length;
    const totalEnrollments = courses.reduce((sum, c) => sum + (c._count?.Enrollments || 0), 0);
    const avgCompletion = 0; // Would need to calculate from enrollment data

    return { total, published, totalEnrollments, avgCompletion };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <div className="course-list-container">
        <div className="loading">Loading courses...</div>
      </div>
    );
  }

  return (
    <div className="course-list-container">
      <div className="course-list-header">
        <h1>My Courses</h1>
        <button className="btn-primary" onClick={() => navigate('/educator/courses/create')}>
          Create New Course
        </button>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>{stats.total}</h3>
          <p>Total Courses</p>
        </div>
        <div className="stat-card">
          <h3>{stats.published}</h3>
          <p>Published</p>
        </div>
        <div className="stat-card">
          <h3>{stats.totalEnrollments}</h3>
          <p>Students Enrolled</p>
        </div>
        <div className="stat-card">
          <h3>{stats.avgCompletion}%</h3>
          <p>Avg Completion</p>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <input
          type="text"
          placeholder="Search courses..."
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          className="search-input"
        />

        <select
          value={filters.category}
          onChange={(e) => setFilters({ ...filters, category: e.target.value })}
          className="filter-select"
        >
          <option value="">All Categories</option>
          <option value="Mathematics">Mathematics</option>
          <option value="English">English</option>
          <option value="Science">Science</option>
          <option value="Social Studies">Social Studies</option>
          <option value="Arts">Arts</option>
        </select>

        <select
          value={filters.level}
          onChange={(e) => setFilters({ ...filters, level: e.target.value })}
          className="filter-select"
        >
          <option value="">All Levels</option>
          <option value="Primary">Primary</option>
          <option value="JSS1">JSS1</option>
          <option value="JSS2">JSS2</option>
          <option value="JSS3">JSS3</option>
          <option value="SS1">SS1</option>
          <option value="SS2">SS2</option>
          <option value="SS3">SS3</option>
        </select>

        <select
          value={filters.published}
          onChange={(e) => setFilters({ ...filters, published: e.target.value })}
          className="filter-select"
        >
          <option value="">All Status</option>
          <option value="true">Published</option>
          <option value="false">Draft</option>
        </select>

        <div className="view-toggle">
          <button
            className={viewMode === 'grid' ? 'active' : ''}
            onClick={() => setViewMode('grid')}
          >
            Grid
          </button>
          <button
            className={viewMode === 'list' ? 'active' : ''}
            onClick={() => setViewMode('list')}
          >
            List
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Courses List */}
      {courses.length === 0 ? (
        <div className="empty-state">
          <h3>No courses yet</h3>
          <p>Create your first course to get started!</p>
          <button className="btn-primary" onClick={() => navigate('/educator/courses/create')}>
            Create Course
          </button>
        </div>
      ) : (
        <div className={`courses-${viewMode}`}>
          {courses.map((course) => (
            <div key={course.id} className="course-card">
              <div className="course-thumbnail">
                {course.thumbnail ? (
                  <img src={course.thumbnail} alt={course.title} />
                ) : (
                  <div className="placeholder-thumbnail">{course.category[0]}</div>
                )}
                <span className={`status-badge ${course.published ? 'published' : 'draft'}`}>
                  {course.published ? 'Published' : 'Draft'}
                </span>
              </div>

              <div className="course-info">
                <h3>{course.title}</h3>
                <p className="course-meta">
                  {course.level} • {course.category} {course.term && `• ${course.term}`}
                </p>
                <p className="course-description">{course.description}</p>

                <div className="course-stats">
                  <span>{course.Modules?.length || 0} Modules</span>
                  <span>
                    {course.Modules?.reduce((sum, m) => sum + (m.Lessons?.length || 0), 0) || 0}{' '}
                    Lessons
                  </span>
                  <span>{course._count?.Enrollments || 0} Students</span>
                  <span>{course.duration}h</span>
                </div>

                <div className="course-actions">
                  <button
                    className="btn-secondary"
                    onClick={() => navigate(`/educator/courses/${course.id}/edit`)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={() => navigate(`/educator/courses/${course.id}/analytics`)}
                  >
                    Analytics
                  </button>
                  <button
                    className={`btn-secondary ${course.published ? 'btn-warning' : 'btn-success'}`}
                    onClick={() => handleTogglePublish(course.id)}
                  >
                    {course.published ? 'Unpublish' : 'Publish'}
                  </button>
                  <button
                    className="btn-danger"
                    onClick={() => handleDelete(course.id, course.title)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default CourseList;
